import {
  Breadcrumb,
  BreadcrumbDivider,
  BreadcrumbItem,
  Hamburger,
  makeStyles,
  Text,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import { Fragment } from 'react';
import { useLocation } from 'react-router';
import { MODULES } from '#src/app/navigation/modules';
import { findRoute } from '#src/app/navigation/navigation';
import { SHELL_HEADER_HEIGHT } from '#src/app/shell/sidebar.styles';
import { UserMenu } from '#src/app/shell/UserMenu';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    // Mesma altura do cabeçalho da navegação, com a divisória alinhada à dele.
    height: SHELL_HEADER_HEIGHT,
    boxSizing: 'border-box',
    padding: `0 ${tokens.spacingHorizontalXXL} 0 ${tokens.spacingHorizontalXXXL}`,
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  trail: { flexGrow: 1, minWidth: 0 },
  moduleText: { color: tokens.colorNeutralForeground3 },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
});

function Current({ label }: { label: string }) {
  return (
    <BreadcrumbItem>
      <Text weight="semibold" aria-current="page">
        {label}
      </Text>
    </BreadcrumbItem>
  );
}

/** Rótulos da trilha: módulo, página e, numa rota interna, o título dela. */
function trailOf(pathname: string): string[] {
  const match = findRoute(MODULES, pathname);
  if (match === undefined) {
    return ['Início'];
  }
  const { module, page, route } = match;
  // Módulo de uma página só: o nome do módulo já diz qual é a página.
  const pageLabel =
    page === undefined || module.pages.length === 1 ? [] : [page.title];
  return [module.title, ...pageLabel, ...(route ? [route.title] : [])];
}

/** O último rótulo é o atual; os anteriores, discretos. */
function Trail() {
  const styles = useStyles();
  const labels = trailOf(useLocation().pathname);
  const current = labels.at(-1) ?? 'Início';
  return (
    <>
      {labels.slice(0, -1).map((label) => (
        <Fragment key={label}>
          <BreadcrumbItem>
            <Text className={styles.moduleText}>{label}</Text>
          </BreadcrumbItem>
          <BreadcrumbDivider />
        </Fragment>
      ))}
      <Current label={current} />
    </>
  );
}

export interface TopBarProps {
  /** Tela estreita: a navegação é uma gaveta, aberta por este botão. */
  readonly showMenu: boolean;
  onOpenMenu(): void;
}

export function TopBar({ showMenu, onOpenMenu }: TopBarProps) {
  const styles = useStyles();
  return (
    <header className={styles.root}>
      {showMenu && (
        <Tooltip content="Abrir navegação" relationship="label">
          <Hamburger onClick={onOpenMenu} />
        </Tooltip>
      )}
      <Breadcrumb className={styles.trail} aria-label="Localização">
        <Trail />
      </Breadcrumb>
      <div className={styles.actions}>
        <UserMenu />
      </div>
    </header>
  );
}
